/**
 * {@link PortalApi} over the real worker.
 *
 * The only module in the portal that knows the overview endpoint exists —
 * the same arrangement `httpAuthApi.ts` has for the auth routes, and what lets
 * every page above it stay a pure function of {@link PortalOverview}.
 */

import type {
  NotificationSettings,
  PlanNotificationOverride,
  PortalOverview,
} from "@bigbear/shared";
import { INHERIT_PLAN_OVERRIDE } from "@bigbear/shared";

import { API_BASE_URL } from "./apiBase";
import { PortalError, type PortalApi, type SummaryPreview } from "./portal";

/**
 * The session cookie is `HttpOnly`, so this code cannot attach it by hand — the
 * browser does, but only when asked. `same-origin` (the default) would send
 * nothing, because the portal and the API are different subdomains.
 */
const CREDENTIALS: RequestCredentials = "include";

/** `Content-Type` is only needed on requests that carry a JSON body — adding
 * it to every request (GETs and DELETEs included) does nothing wrong, but it
 * would be one more header to explain on the requests that have no body to
 * describe. */
const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

export function createHttpPortalApi(baseUrl: string = API_BASE_URL): PortalApi {
  return {
    isDemo: false,

    async overview() {
      const response = await fetch(`${baseUrl}/v1/overview`, {
        credentials: CREDENTIALS,
        headers: { Accept: "application/json" },
      });

      // `RequireAuth` has already gated this route, so a 401 here means the
      // session expired while the page was open rather than that the visitor
      // was never signed in — worth saying, because "sign in again" is
      // actionable and "could not load your backups" is not.
      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to see your backups.",
        );
      }

      const body: unknown = await response.json().catch(() => null);

      if (!response.ok) {
        throw new PortalError(messageFrom(body, response.status));
      }

      return body as PortalOverview;
    },

    async removeMachine(machineId) {
      const response = await fetch(
        `${baseUrl}/v1/devices/${encodeURIComponent(machineId)}`,
        {
          method: "DELETE",
          credentials: CREDENTIALS,
          headers: { Accept: "application/json" },
        },
      );

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to remove a machine.",
        );
      }

      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        throw new PortalError(messageFrom(body, response.status));
      }
    },

    // `machineId` is unused here — a plan id is already unique per account,
    // so the worker needs nothing else to find it. It stays in the call
    // signature because the demo source, which has no server to ask, does
    // need it to disambiguate.
    async clearJobHistory(_machineId, jobId) {
      const response = await fetch(
        `${baseUrl}/v1/plans/${encodeURIComponent(jobId)}/runs`,
        {
          method: "DELETE",
          credentials: CREDENTIALS,
          headers: { Accept: "application/json" },
        },
      );

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to clear this job's history.",
        );
      }

      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        throw new PortalError(messageFrom(body, response.status));
      }
    },

    async notificationSettings() {
      const response = await fetch(`${baseUrl}/v1/notifications/settings`, {
        credentials: CREDENTIALS,
        headers: { Accept: "application/json" },
      });

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to see your alert settings.",
        );
      }

      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new PortalError(
          messageFrom(
            body,
            response.status,
            "Could not load your alert settings",
          ),
        );
      }

      return (body as { settings: NotificationSettings }).settings;
    },

    async saveNotificationSettings(settings) {
      const response = await fetch(`${baseUrl}/v1/notifications/settings`, {
        method: "POST",
        credentials: CREDENTIALS,
        headers: JSON_HEADERS,
        body: JSON.stringify(settings),
      });

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to save your alert settings.",
        );
      }

      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new PortalError(
          messageFrom(
            body,
            response.status,
            "Could not save your alert settings",
          ),
        );
      }

      return (body as { settings: NotificationSettings }).settings;
    },

    async planNotificationOverride(planId) {
      const response = await fetch(
        `${baseUrl}/v1/notifications/plans/${encodeURIComponent(planId)}`,
        {
          credentials: CREDENTIALS,
          headers: { Accept: "application/json" },
        },
      );

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to see this plan's alert settings.",
        );
      }

      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new PortalError(
          messageFrom(
            body,
            response.status,
            "Could not load this plan's alert settings",
          ),
        );
      }

      return (body as { override: PlanNotificationOverride | null }).override;
    },

    async savePlanNotificationOverride(planId, override) {
      const response = await fetch(
        `${baseUrl}/v1/notifications/plans/${encodeURIComponent(planId)}`,
        {
          method: "POST",
          credentials: CREDENTIALS,
          headers: JSON_HEADERS,
          body: JSON.stringify(override),
        },
      );

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to save this plan's alert settings.",
        );
      }

      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new PortalError(
          messageFrom(
            body,
            response.status,
            "Could not save this plan's alert settings",
          ),
        );
      }

      // An all-null override is a delete on the server, which answers with
      // `{override: null}` rather than echoing the nulls back — the caller's
      // contract promises a concrete override regardless, so an absent one
      // means "inherit everything", exactly what was just asked for.
      return (
        (body as { override: PlanNotificationOverride | null }).override ??
        INHERIT_PLAN_OVERRIDE
      );
    },

    async clearPlanNotificationOverride(planId) {
      const response = await fetch(
        `${baseUrl}/v1/notifications/plans/${encodeURIComponent(planId)}`,
        {
          method: "DELETE",
          credentials: CREDENTIALS,
          headers: { Accept: "application/json" },
        },
      );

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to clear this plan's alert settings.",
        );
      }

      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        throw new PortalError(
          messageFrom(
            body,
            response.status,
            "Could not clear this plan's alert settings",
          ),
        );
      }
    },

    async summaryPreview(cadence) {
      const response = await fetch(
        `${baseUrl}/v1/notifications/summary/preview?cadence=${cadence}`,
        {
          credentials: CREDENTIALS,
          headers: { Accept: "application/json" },
        },
      );

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to preview your summary.",
        );
      }

      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new PortalError(
          messageFrom(body, response.status, "Could not build a preview"),
        );
      }

      return body as SummaryPreview;
    },

    async sendSummaryTest(channel, cadence) {
      const response = await fetch(`${baseUrl}/v1/notifications/summary/test`, {
        method: "POST",
        credentials: CREDENTIALS,
        headers: JSON_HEADERS,
        body: JSON.stringify({ channel, cadence }),
      });

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to send a test summary.",
        );
      }

      if (!response.ok) {
        // 429 included: "you already sent yourself one today" is the answer
        // the button asked for, not an error to paper over with a generic one.
        const body: unknown = await response.json().catch(() => null);
        throw new PortalError(
          messageFrom(body, response.status, "Could not send the test summary"),
        );
      }
    },

    async sendNotificationTest(channel, webhookUrl) {
      const response = await fetch(`${baseUrl}/v1/notifications/test`, {
        method: "POST",
        credentials: CREDENTIALS,
        headers: JSON_HEADERS,
        body: JSON.stringify({ channel, webhookUrl }),
      });

      if (response.status === 401) {
        throw new PortalError(
          "Your session has expired. Sign in again to send a test alert.",
        );
      }

      if (!response.ok) {
        // The worker's `test_failed` error carries the reason the webhook
        // itself gave (or timed out with) in `message` — that's the whole
        // point of a test-send, so it has to reach the button that asked.
        const body: unknown = await response.json().catch(() => null);
        throw new PortalError(
          messageFrom(body, response.status, "Could not send the test alert"),
        );
      }
    },
  };
}

/**
 * The worker's own wording where it gave some.
 *
 * Both envelope shapes are read for the same reason `httpAuthApi` reads both:
 * Better Auth writes `{ message, code }` and the hand-written handlers write
 * `{ error, message }`, and a caller should not have to know which kind of
 * route it hit.
 */
function messageFrom(
  body: unknown,
  status: number,
  fallback: string = "Could not load your backups",
): string {
  const detail = body as { message?: string; error?: string } | null;

  return detail?.message ?? `${fallback} (HTTP ${status}).`;
}
